import { 
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase.js';

// Collection names with sigma- prefix
export const COLLECTIONS = {
  USERS: 'sigma-users',
  DOCUMENTS: 'sigma-documents',
  ASSESSMENTS: 'sigma-assessments',
  SUBMISSIONS: 'sigma-assessment-submissions',
  SHARES: 'sigma-shares',
  SETTINGS: 'sigma-settings',
  GUIDES: 'sigma-guides',
  TOOLS: 'sigma-tools',
  LEARNERS: 'sigma-learners',
  MARKING_JOBS: 'sigma-marking-jobs',
  MARKING_TASKS: 'sigma-tasks',
  MARKING_QUESTIONS: 'sigma-questions'
};

const normalizeRole = (value) => String(value || '').trim().toLowerCase();

const getSigmaUserRole = (user = {}) =>
  normalizeRole(user.userRole || user.role);

const sortUsersByName = (users = []) =>
  [...users].sort((a, b) => {
    const aName = String(
      a.displayName ||
      [a.firstName, a.lastName].filter(Boolean).join(' ') ||
      a.email ||
      ''
    ).toLowerCase();
    const bName = String(
      b.displayName ||
      [b.firstName, b.lastName].filter(Boolean).join(' ') ||
      b.email ||
      ''
    ).toLowerCase();
    return aName.localeCompare(bName);
  });

const DOCUMENT_PAGES_SUBCOLLECTION = 'pages';
const pageDocId = (index) => String(index);

const saveDocumentPages = async (documentId, pages, previousCount = null) => {
  const batch = writeBatch(db);
  const pagesRef = collection(db, COLLECTIONS.DOCUMENTS, documentId, DOCUMENT_PAGES_SUBCOLLECTION);

  if (previousCount == null) {
    const existing = await getDocs(pagesRef);
    existing.forEach((snap) => batch.delete(snap.ref));
  } else if (previousCount > pages.length) {
    for (let i = pages.length; i < previousCount; i += 1) {
      batch.delete(doc(pagesRef, pageDocId(i)));
    }
  }

  pages.forEach((html, index) => {
    const pageRef = doc(pagesRef, pageDocId(index));
    batch.set(pageRef, { index, html, updatedAt: serverTimestamp() });
  });

  await batch.commit();
};

const loadDocumentPages = async (documentId) => {
  const pagesRef = collection(db, COLLECTIONS.DOCUMENTS, documentId, DOCUMENT_PAGES_SUBCOLLECTION);
  const q = query(pagesRef, orderBy('index', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((pageDoc) => pageDoc.data().html || '');
};

export class FirestoreService {
  static buildMentorAssignment(mentor, existingAssignment = null, type = 'teacher') {
    if (!mentor?.id) return null;
    const previousUserId = existingAssignment?.userId || null;
    const previousStatus = String(existingAssignment?.status || '').trim().toLowerCase();
    const isSameMentor = previousUserId === mentor.id;
    const status = isSameMentor && previousStatus ? previousStatus : 'pending';
    return {
      userId: mentor.id,
      displayName:
        mentor.displayName ||
        [mentor.firstName, mentor.lastName].filter(Boolean).join(' ').trim() ||
        mentor.email ||
        `${type === 'tutor' ? 'Tutor' : 'Teacher'} ${mentor.id}`,
      email: mentor.email || '',
      role: getSigmaUserRole(mentor) || type,
      school: mentor.school || '',
      project: mentor.project || '',
      status,
      requestedAt: existingAssignment?.requestedAt || new Date().toISOString(),
      confirmedAt: isSameMentor && status === 'confirmed' ? (existingAssignment?.confirmedAt || null) : null,
      confirmedBy: isSameMentor && status === 'confirmed' ? (existingAssignment?.confirmedBy || mentor.id) : null,
    };
  }

  // Document operations
  static async createDocument(userId, documentData) {
    try {
      const pages = Array.isArray(documentData.pages) && documentData.pages.length > 0
        ? documentData.pages
        : [''];

      const { pages: _pages, ...payload } = documentData;
      const docRef = await addDoc(collection(db, COLLECTIONS.DOCUMENTS), {
        ...payload,
        pageCount: pages.length,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await saveDocumentPages(docRef.id, pages);
      return docRef.id;
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  }

  static async updateDocument(documentId, updates) {
    try {
      const pages = Array.isArray(updates.pages) ? updates.pages : null;
      if (pages) {
        await saveDocumentPages(documentId, pages);
      }
      const updatePayload = { ...updates };
      if (pages) delete updatePayload.pages;
      const sanitizedPayload = Object.fromEntries(
        Object.entries(updatePayload).filter(([, value]) => value !== undefined)
      );
      const docRef = doc(db, COLLECTIONS.DOCUMENTS, documentId);
      const docUpdates = {
        ...sanitizedPayload,
        updatedAt: serverTimestamp()
      };
      if (pages) {
        docUpdates.pageCount = pages.length;
      } else if (typeof updates.pageCount === 'number') {
        docUpdates.pageCount = updates.pageCount;
      }
      await updateDoc(docRef, docUpdates);
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  static async getDocument(documentId) {
    try {
      const docRef = doc(db, COLLECTIONS.DOCUMENTS, documentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        let pages = [];
        try {
          pages = await loadDocumentPages(docSnap.id);
        } catch (err) {
          console.warn('Error loading pages subcollection:', err);
        }
        return {
          id: docSnap.id,
          ...docSnap.data(),
          pages: pages.length ? pages : (docSnap.data().pages || ['']),
          pageCount: pages.length ? pages.length : (docSnap.data().pageCount || (docSnap.data().pages?.length ?? 1)),
          narration: docSnap.data().narration || null
        };
      } else {
        throw new Error('Document not found');
      }
    } catch (error) {
      console.error('Error getting document:', error);
      throw error;
    }
  }

  static async getUserDocuments(userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.DOCUMENTS),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting user documents:', error);
      throw error;
    }
  }

  static async createGeometryDiagram(userId, diagramData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.DOCUMENTS), {
        ...diagramData,
        contentType: 'geometry-diagram',
        toolType: 'geometry-tool',
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating geometry diagram:', error);
      throw error;
    }
  }

  static async updateGeometryDiagram(diagramId, updates) {
    try {
      const docRef = doc(db, COLLECTIONS.DOCUMENTS, diagramId);
      const sanitizedPayload = Object.fromEntries(
        Object.entries(updates).filter(([, value]) => value !== undefined)
      );
      await updateDoc(docRef, {
        ...sanitizedPayload,
        contentType: 'geometry-diagram',
        toolType: 'geometry-tool',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating geometry diagram:', error);
      throw error;
    }
  }

  static async getGeometryDiagram(diagramId) {
    try {
      const snapshot = await getDoc(doc(db, COLLECTIONS.DOCUMENTS, diagramId));
      if (!snapshot.exists()) {
        throw new Error('Diagram not found');
      }

      return {
        id: snapshot.id,
        ...snapshot.data()
      };
    } catch (error) {
      console.error('Error getting geometry diagram:', error);
      throw error;
    }
  }

  static async getUserGeometryDiagrams(userId) {
    try {
      const snapshot = await getDocs(query(
        collection(db, COLLECTIONS.DOCUMENTS),
        where('userId', '==', userId)
      ));

      return snapshot.docs
        .map((diagramDoc) => ({
          id: diagramDoc.id,
          ...diagramDoc.data()
        }))
        .filter((diagram) => diagram.contentType === 'geometry-diagram')
        .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
    } catch (error) {
      console.error('Error getting user geometry diagrams:', error);
      throw error;
    }
  }

  static async deleteGeometryDiagram(diagramId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.DOCUMENTS, diagramId));
    } catch (error) {
      console.error('Error deleting geometry diagram:', error);
      throw error;
    }
  }

  static async createGraphSketch(userId, sketchData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.DOCUMENTS), {
        ...sketchData,
        contentType: 'graph-sketch',
        toolType: 'graph-sketch-tool',
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating graph sketch:', error);
      throw error;
    }
  }

  static async updateGraphSketch(sketchId, updates) {
    try {
      const docRef = doc(db, COLLECTIONS.DOCUMENTS, sketchId);
      const sanitizedPayload = Object.fromEntries(
        Object.entries(updates).filter(([, value]) => value !== undefined)
      );
      await updateDoc(docRef, {
        ...sanitizedPayload,
        contentType: 'graph-sketch',
        toolType: 'graph-sketch-tool',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating graph sketch:', error);
      throw error;
    }
  }

  static async getGraphSketch(sketchId) {
    try {
      const snapshot = await getDoc(doc(db, COLLECTIONS.DOCUMENTS, sketchId));
      if (!snapshot.exists()) {
        throw new Error('Graph sketch not found');
      }

      return {
        id: snapshot.id,
        ...snapshot.data()
      };
    } catch (error) {
      console.error('Error getting graph sketch:', error);
      throw error;
    }
  }

  static async getUserGraphSketches(userId) {
    try {
      const snapshot = await getDocs(query(
        collection(db, COLLECTIONS.DOCUMENTS),
        where('userId', '==', userId)
      ));

      return snapshot.docs
        .map((sketchDoc) => ({
          id: sketchDoc.id,
          ...sketchDoc.data()
        }))
        .filter((sketch) => sketch.contentType === 'graph-sketch')
        .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
    } catch (error) {
      console.error('Error getting user graph sketches:', error);
      throw error;
    }
  }

  static async deleteGraphSketch(sketchId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.DOCUMENTS, sketchId));
    } catch (error) {
      console.error('Error deleting graph sketch:', error);
      throw error;
    }
  }

  static async deleteDocument(documentId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.DOCUMENTS, documentId));
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  // Assessment operations
  static async createAssessment(userId, assessmentData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.ASSESSMENTS), {
        ...assessmentData,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating assessment:', error);
      throw error;
    }
  }

  static async getAssessment(assessmentId) {
    try {
      const docRef = doc(db, COLLECTIONS.ASSESSMENTS, assessmentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Assessment not found');
      }
    } catch (error) {
      console.error('Error getting assessment:', error);
      throw error;
    }
  }

  // Share operations
  static async createShare(documentId, shareData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.SHARES), {
        documentId,
        ...shareData,
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating share:', error);
      throw error;
    }
  }

  static async getShare(shareId) {
    try {
      const docRef = doc(db, COLLECTIONS.SHARES, shareId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Share not found');
      }
    } catch (error) {
      console.error('Error getting share:', error);
      throw error;
    }
  }

  // Assessment submission operations
  static async createSubmission(submissionData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.SUBMISSIONS), {
        ...submissionData,
        createdAt: serverTimestamp(),
        lastSavedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating submission:', error);
      throw error;
    }
  }

  static async getSubmission(submissionId) {
    try {
      const docRef = doc(db, COLLECTIONS.SUBMISSIONS, submissionId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting submission:', error);
      throw error;
    }
  }

  static async updateSubmission(submissionId, updates) {
    try {
      const docRef = doc(db, COLLECTIONS.SUBMISSIONS, submissionId);
      await updateDoc(docRef, {
        ...updates,
        lastSavedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating submission:', error);
      throw error;
    }
  }

  static async getSubmissionByDocAndLearner(documentId, learnerId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.SUBMISSIONS),
        where('documentId', '==', documentId),
        where('learnerId', '==', learnerId)
      );
      const snap = await getDocs(q);
      if (snap.empty) return null;
      const getSubmissionSortTime = (submission) => {
        const timestamp =
          submission.lastSavedAt ||
          submission.createdAt ||
          submission.startedAt ||
          null;
        if (timestamp?.seconds) return timestamp.seconds * 1000;
        if (typeof timestamp === 'string') return Date.parse(timestamp) || 0;
        return 0;
      };
      const submissions = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => getSubmissionSortTime(b) - getSubmissionSortTime(a));
      const d = submissions[0];
      return d;
    } catch (error) {
      console.error('Error getting submission by doc and learner:', error);
      throw error;
    }
  }

  // --- Additional share operations ---

  static async getSharesForDocument(documentId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.SHARES),
        where('documentId', '==', documentId)
      );
      const snap = await getDocs(q);
      const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      results.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      return results;
    } catch (error) {
      console.error('Error getting shares for document:', error);
      throw error;
    }
  }

  static async getSharesForRecipient(recipientId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.SHARES),
        where('recipientId', '==', recipientId)
      );
      const snap = await getDocs(q);
      const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      results.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      return results;
    } catch (error) {
      console.error('Error getting shares for recipient:', error);
      throw error;
    }
  }

  static async getShareByToken(linkToken) {
    try {
      const q = query(
        collection(db, COLLECTIONS.SHARES),
        where('linkToken', '==', linkToken),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) return null;
      const d = snap.docs[0];
      return { id: d.id, ...d.data() };
    } catch (error) {
      console.error('Error getting share by token:', error);
      throw error;
    }
  }

  static async deleteShare(shareId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.SHARES, shareId));
    } catch (error) {
      console.error('Error deleting share:', error);
      throw error;
    }
  }

  static async updateShare(shareId, updates) {
    try {
      const docRef = doc(db, COLLECTIONS.SHARES, shareId);
      await updateDoc(docRef, updates);
    } catch (error) {
      console.error('Error updating share:', error);
      throw error;
    }
  }

  static async addDocumentShare(documentId, recipientId, data) {
    try {
      const shareRef = doc(db, COLLECTIONS.DOCUMENTS, documentId, 'sharedUsers', recipientId);
      await setDoc(shareRef, {
        recipientId,
        ...data,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error creating document share:', error);
      throw error;
    }
  }

  static async removeDocumentShare(documentId, recipientId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.DOCUMENTS, documentId, 'sharedUsers', recipientId));
    } catch (error) {
      console.error('Error removing document share:', error);
      throw error;
    }
  }

  static async findUsersByInstitution(institutionName, excludeUid) {
    try {
      const q = query(
        collection(db, COLLECTIONS.USERS),
        where('institutionName', '==', institutionName)
      );
      const snap = await getDocs(q);
      return snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.id !== excludeUid);
    } catch (error) {
      console.error('Error finding users by institution:', error);
      throw error;
    }
  }

  static async findUsersByField(field, value) {
    if (!String(value || '').trim()) return [];
    try {
      const q = query(
        collection(db, COLLECTIONS.USERS),
        where(field, '==', String(value).trim())
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error(`Error finding users by ${field}:`, error);
      throw error;
    }
  }

  static async findMentorCandidates(context = {}, excludeUid = null) {
    try {
      const institutionName = String(context.institutionName || '').trim();
      const school = String(context.school || '').trim();
      const project = String(context.project || '').trim();

      const queryResults = await Promise.all([
        this.findUsersByField('institutionName', institutionName),
        this.findUsersByField('school', school),
        this.findUsersByField('project', project),
      ]);

      const merged = new Map();
      queryResults.flat().forEach((entry) => {
        if (entry?.id) merged.set(entry.id, entry);
      });

      const matchesContext = (candidate) => {
        const candidateSchool = String(candidate.school || '').trim().toLowerCase();
        const candidateProject = String(candidate.project || '').trim().toLowerCase();
        const candidateInstitution = String(candidate.institutionName || '').trim().toLowerCase();
        const schoolKey = school.toLowerCase();
        const projectKey = project.toLowerCase();
        const institutionKey = institutionName.toLowerCase();

        if (schoolKey && candidateSchool && candidateSchool === schoolKey) return true;
        if (projectKey && candidateProject && candidateProject === projectKey) return true;
        if (!schoolKey && !projectKey && institutionKey && candidateInstitution === institutionKey) return true;
        return false;
      };

      const all = sortUsersByName(
        [...merged.values()].filter((candidate) => {
          if (!candidate?.id || candidate.id === excludeUid) return false;
          const role = getSigmaUserRole(candidate);
          if (!['teacher', 'tutor'].includes(role)) return false;
          return matchesContext(candidate);
        })
      );

      return {
        all,
        teachers: all.filter((candidate) => getSigmaUserRole(candidate) === 'teacher'),
        tutors: all.filter((candidate) => getSigmaUserRole(candidate) === 'tutor'),
      };
    } catch (error) {
      console.error('Error finding mentor candidates:', error);
      throw error;
    }
  }

  static async getLearnerAssignmentRequestsForMentor(mentorId) {
    try {
      if (!mentorId) return [];
      const [teacherSnap, tutorSnap] = await Promise.all([
        getDocs(query(collection(db, COLLECTIONS.USERS), where('teacherAssignment.userId', '==', mentorId))),
        getDocs(query(collection(db, COLLECTIONS.USERS), where('tutorAssignment.userId', '==', mentorId))),
      ]);

      const requests = new Map();
      [teacherSnap, tutorSnap].forEach((snap) => {
        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const role = getSigmaUserRole(data);
          if (!['learner', 'student'].includes(role)) return;
          requests.set(docSnap.id, {
            id: docSnap.id,
            ...data,
          });
        });
      });

      return sortUsersByName([...requests.values()]);
    } catch (error) {
      console.error('Error getting learner assignment requests for mentor:', error);
      throw error;
    }
  }

  static async updateLearnerMentorAssignmentStatus(learnerId, assignmentType, updates = {}) {
    try {
      if (!learnerId || !assignmentType) {
        throw new Error('Missing learnerId or assignmentType');
      }
      const key = assignmentType === 'tutor' ? 'tutorAssignment' : 'teacherAssignment';
      const learnerRef = doc(db, COLLECTIONS.USERS, learnerId);
      const learnerSnap = await getDoc(learnerRef);
      if (!learnerSnap.exists()) {
        throw new Error('Learner profile not found');
      }
      const currentData = learnerSnap.data() || {};
      const currentAssignment = currentData[key] || null;
      if (!currentAssignment?.userId) {
        throw new Error('Learner has no linked mentor for this assignment');
      }

      await updateDoc(learnerRef, {
        [key]: {
          ...currentAssignment,
          ...updates,
        },
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating learner mentor assignment status:', error);
      throw error;
    }
  }

  static async getSubmissionsForDocument(documentId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.SUBMISSIONS),
        where('documentId', '==', documentId),
        orderBy('submittedAt', 'desc')
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error getting submissions for document:', error);
      throw error;
    }
  }

  static async getGradingInboxSubmissions(userId) {
    try {
      if (!userId) return [];

      const isSubmittedAttempt = (item) => {
        const status = String(item?.status || '').trim().toLowerCase();
        return status === 'submitted' || status === 'timed-out';
      };

      const runQuery = async (field) => {
        const q = query(
          collection(db, COLLECTIONS.SUBMISSIONS),
          where(field, '==', userId)
        );
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      };

      const [teacherItems, tutorItems, ownerItems] = await Promise.all([
        runQuery('submittedToTeacherId'),
        runQuery('submittedToTutorId'),
        runQuery('documentOwnerId'),
      ]);

      const merged = new Map();
      [...teacherItems, ...tutorItems, ...ownerItems].forEach((item) => {
        if (!item?.id) return;
        if (item.requiresGrading === false) return;
        if (!isSubmittedAttempt(item)) return;
        merged.set(item.id, item);
      });

      return [...merged.values()].sort((a, b) => {
        const aTime = a.submittedAt?.seconds || a.lastSavedAt?.seconds || a.createdAt?.seconds || 0;
        const bTime = b.submittedAt?.seconds || b.lastSavedAt?.seconds || b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
    } catch (error) {
      console.error('Error getting grading inbox submissions:', error);
      throw error;
    }
  }

  static async saveSubmissionGrade(submissionId, gradeData = {}) {
    try {
      const docRef = doc(db, COLLECTIONS.SUBMISSIONS, submissionId);
      await updateDoc(docRef, {
        ...gradeData,
        lastSavedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error saving submission grade:', error);
      throw error;
    }
  }
}
